import { useEffect, useRef, useState } from "react";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { TabView, TabPanel } from 'primereact/tabview';
import PromissoryNoteService from '../../services/PromissoryNoteService';
import { useSearchParams } from 'react-router-dom';

export default function PromissoryNoteListPage() {
    const [workTrackingParams, setWorkTrackingParams] = useSearchParams();
    const [highlightedNoteId] = useState(() => Number(workTrackingParams.get('highlight')));
    const workTrackingHighlightHandled = useRef(false);
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [globalFilter, setGlobalFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState(null);
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        return new Date(d.getFullYear(), d.getMonth(), 1);
    });
    const [endDate, setEndDate] = useState(() => {
        const d = new Date();
        return new Date(d.getFullYear(), d.getMonth() + 1, 0);
    });
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const toast = useRef(null);

    const statusOptions = [
        { label: 'Tüm Durumlar', value: null },
        { label: 'Portföyde', value: 1 },
        { label: 'Ciro Edildi', value: 2 },
        { label: 'Tahsil Edildi', value: 3 },
        { label: 'Ödendi', value: 4 },
        { label: 'İade Edildi', value: 5 }
    ];

    const clearFilters = () => {
        const d = new Date();
        const start = new Date(d.getFullYear(), d.getMonth(), 1);
        const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
        setGlobalFilter('');
        setStatusFilter(null);
        setStartDate(start);
        setEndDate(end);
        fetchNotes(null, start, end);
    };

    const formatDateParam = (date) => {
        if (!date) return null;
        const d = new Date(date);
        return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
    };

    const fetchNotes = async (status = statusFilter, start = startDate, end = endDate) => {
        try {
            setLoading(true);
            const res = await PromissoryNoteService.getPromissoryNotes(status, null, formatDateParam(start), formatDateParam(end));
            setNotes(res.data || []);
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Senetler yüklenirken hata oluştu' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNotes();
    }, []);

    useEffect(() => {
        if (workTrackingHighlightHandled.current || loading || !Number.isFinite(highlightedNoteId) || highlightedNoteId <= 0) return;
        const highlighted = notes.find((item) => Number(item.id) === highlightedNoteId);
        if (highlighted) setActiveTabIndex(highlighted.type === 2 ? 1 : 0);
        workTrackingHighlightHandled.current = true;
        const nextParams = new URLSearchParams(workTrackingParams);
        nextParams.delete('highlight');
        setWorkTrackingParams(nextParams, { replace: true });
    }, [highlightedNoteId, loading, notes, setWorkTrackingParams, workTrackingParams]);

    const updateStatus = async (id, newStatus) => {
        try {
            const res = await PromissoryNoteService.updateStatus({ promissoryNoteId: id, newStatus });
            if (res.success) {
                toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'Senet durumu güncellendi' });
                fetchNotes();
            }
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Durum güncellenirken hata oluştu' });
        }
    };

    const statusBodyTemplate = (rowData) => {
        const statuses = {
            1: { label: 'Portföyde', severity: 'info' },
            2: { label: 'Ciro Edildi', severity: 'warning' },
            3: { label: 'Tahsil Edildi', severity: 'success' },
            4: { label: 'Ödendi', severity: 'success' },
            5: { label: 'İade Edildi', severity: 'danger' }
        };
        const status = statuses[rowData.status] || { label: 'Bilinmiyor', severity: 'secondary' };
        return <Tag value={status.label} severity={status.severity} />;
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex gap-2">
                {rowData.status === 1 ? (
                    <>
                        {rowData.type === 1 && <Button icon="pi pi-check" className="p-button-rounded p-button-success p-button-text" tooltip="Tahsil Edildi" onClick={() => updateStatus(rowData.id, 3)} />}
                        {rowData.type === 2 && <Button icon="pi pi-money-bill" className="p-button-rounded p-button-success p-button-text" tooltip="Ödendi Olarak İşaretle" onClick={() => updateStatus(rowData.id, 4)} />}
                        <Button icon="pi pi-replay" className="p-button-rounded p-button-danger p-button-text" tooltip="İade Et" onClick={() => updateStatus(rowData.id, 5)} />
                    </>
                ) : (
                    <Button icon="pi pi-undo" className="p-button-rounded p-button-warning p-button-text" tooltip="Geri Al (Portföye)" onClick={() => updateStatus(rowData.id, 1)} />
                )}
            </div>
        );
    };

    const prioritizeAndFilter = (rows) => {
        const filtered = rows.filter((row) => {
            if (statusFilter !== null && Number(row.status) !== Number(statusFilter)) {
                return false;
            }
            if (startDate) {
                const due = new Date(row.dueDate);
                const start = new Date(startDate);
                start.setHours(0, 0, 0, 0);
                if (due < start) return false;
            }
            if (endDate) {
                const due = new Date(row.dueDate);
                const end = new Date(endDate);
                end.setHours(23, 59, 59, 999);
                if (due > end) return false;
            }
            return true;
        });

        return filtered.sort((left, right) => {
            if (Number(left.id) === highlightedNoteId) return -1;
            if (Number(right.id) === highlightedNoteId) return 1;
            return 0;
        });
    };

    const inboundNotes = prioritizeAndFilter(notes.filter(x => x.type === 1));
    const outboundNotes = prioritizeAndFilter(notes.filter(x => x.type === 2));
    const rowClassName = (row) => Number(row.id) === highlightedNoteId ? '!bg-amber-50 ring-1 ring-inset ring-amber-300' : '';

    const renderHeader = () => {
        return (
            <div className="flex justify-content-end">
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText
                        value={globalFilter}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                        placeholder="Müşteri, borçlu veya açıklama ara..."
                        className="p-inputtext-sm !py-2.5 !rounded-xl text-xs font-bold w-64"
                    />
                </span>
            </div>
        );
    };

    const header = renderHeader();

    return (
        <div className="p-4 space-y-4 animate-fade-in">
            <Toast ref={toast}  appendTo={mounted ? document.body : "self"} />
            <div className="bg-white shadow-xl border border-slate-200 rounded-3xl overflow-hidden p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                    <div className="flex items-center gap-3">
                        <div className="!bg-orange-600 p-4 rounded-xl shadow-lg shadow-orange-600/20">
                            <i className="pi pi-file text-white text-xl"></i>
                        </div>
                        <div>
                            <h1 className="text-xl font-black text-slate-800 dark:text-white m-0">Senet Listesi</h1>
                            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Alınan ve verilen tüm senetlerin takibi</p>
                        </div>
                    </div>
                    <Button
                        icon="pi pi-refresh"
                        className="p-button-text p-button-secondary rounded-xl hover:bg-slate-100"
                        onClick={fetchNotes}
                        tooltip="Yenile"
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 bg-slate-50/80 dark:bg-slate-900/50 p-4 rounded-2xl border border-slate-200/80 dark:border-slate-800 items-end mb-6">
                    <div className="md:col-span-3 flex flex-col gap-1.5">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider ml-1">Senet Durumu</label>
                        <Dropdown
                            value={statusFilter}
                            options={statusOptions}
                            onChange={(e) => setStatusFilter(e.value)}
                            placeholder="Tüm Durumlar"
                            className="w-full rounded-xl border-slate-200 font-bold text-sm h-[42px] flex items-center"
                        />
                    </div>
                    <div className="md:col-span-3 flex flex-col gap-1.5">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider ml-1">Vade Başlangıç</label>
                        <Calendar
                            value={startDate}
                            onChange={(e) => setStartDate(e.value)}
                            showIcon
                            dateFormat="dd.mm.yy"
                            placeholder="Tarih seçin"
                            className="w-full"
                            inputClassName="rounded-xl border-slate-200 py-2.5 font-bold text-sm"
                        />
                    </div>
                    <div className="md:col-span-3 flex flex-col gap-1.5">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider ml-1">Vade Bitiş</label>
                        <Calendar
                            value={endDate}
                            onChange={(e) => setEndDate(e.value)}
                            showIcon
                            dateFormat="dd.mm.yy"
                            placeholder="Tarih seçin"
                            className="w-full"
                            inputClassName="rounded-xl border-slate-200 py-2.5 font-bold text-sm"
                        />
                    </div>
                    <div className="md:col-span-3 flex gap-2">
                        <Button
                            label="Ara"
                            icon="pi pi-search"
                            className="flex-1 !bg-orange-600 border-none hover:!bg-orange-700 rounded-xl font-black text-xs py-3 shadow-md shadow-orange-600/20 text-white"
                            onClick={() => fetchNotes()}
                            loading={loading}
                        />
                        <Button
                            label="Temizle"
                            icon="pi pi-filter-slash"
                            outlined
                            severity="secondary"
                            className="!rounded-xl font-black text-xs py-3"
                            onClick={clearFilters}
                        />
                    </div>
                </div>

                <TabView className="compact-tabview" activeIndex={activeTabIndex} onTabChange={(event) => setActiveTabIndex(event.index)}>
                    <TabPanel header="Müşteriden Alınan Senetler" leftIcon="pi pi-arrow-down-left mr-2">
                        <DataTable value={inboundNotes} loading={loading} paginator rows={10} globalFilter={globalFilter} header={header} dataKey="id" rowClassName={rowClassName} responsiveLayout="scroll" className="p-datatable-sm mt-2">
                            <Column field="dueDate" header="Vade" body={(row) => new Date(row.dueDate).toLocaleDateString('tr-TR')} sortable />
                            <Column field="customerName" header="Müşteri" sortable />
                            <Column field="supplierName" header="Ciro Edilen Tedarikçi" sortable />
                            <Column field="drawer" header="Borçlu" sortable />
                            <Column field="amount" header="Tutar" body={(row) => `₺${row.amount.toLocaleString('tr-TR')}`} sortable />
                            <Column field="status" header="Durum" body={statusBodyTemplate} sortable />
                            <Column header="İşlem" body={actionBodyTemplate} />
                        </DataTable>
                    </TabPanel>
                    <TabPanel header="Verilen Senetler" leftIcon="pi pi-arrow-up-right mr-2">
                        <DataTable value={outboundNotes} loading={loading} paginator rows={10} globalFilter={globalFilter} header={header} dataKey="id" rowClassName={rowClassName}
                            responsiveLayout="scroll" className="p-datatable-sm mt-2">
                            <Column field="issueDate" header="Veriliş Tarihi" body={(row) => new Date(row.issueDate).toLocaleDateString('tr-TR')} sortable />
                            <Column field="dueDate" header="Vade" body={(row) => new Date(row.dueDate).toLocaleDateString('tr-TR')} sortable />
                            <Column header="Alıcı (Tedarikçi/Müşteri)" body={(row) => row.supplierName || row.customerName || '-'} sortable sortField="supplierName" />
                            <Column field="drawer" header="Borçlu" sortable />
                            <Column field="amount" header="Tutar" body={(row) => `₺${row.amount.toLocaleString('tr-TR')}`} sortable />
                            <Column field="status" header="Durum" body={statusBodyTemplate} sortable />
                            <Column header="İşlem" body={actionBodyTemplate} />
                        </DataTable>
                    </TabPanel>
                </TabView>
            </div>
        </div>
    );
}
